import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, FormControlLabel, Switch, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useTheme } from '@mui/material';
import { subscribeAnchor } from './search/WebshopConfigSearch';
import Colors from '../../library/Colors';

/**
 * Design variants for the webshop-admin configuration screens.
 *
 * The admin is used by two very different people: a shop owner who opens a
 * settings modal a few times a year and wants to be sure what a switch does,
 * and a SoftTouch consultant who configures many shops in a row and wants
 * density and speed. One chrome cannot serve both, so the shared primitives
 * (ConfigCard, Section, PdiRow, SubLabel, the dialog frame) read their look
 * from here and can be switched at runtime — for demos first, later maybe as
 * a per-user preference.
 *
 *   classic   what the admin renders today — the baseline to compare against.
 *   werkblad  a reading column of at most 720px with a sticky section index
 *             beside it; the dialog shrinks to fit. Topics are headed blocks
 *             separated by hairlines, not boxed cards. Every control ends on
 *             the same right edge of the column, so the eye reads label →
 *             value on a short horizontal. Built for reading and checking.
 *   tabel     the settings as one continuous sheet under a dark title strip:
 *             each topic is a header row, settings are 13px rows on a fixed
 *             label | control grid (all controls right-aligned in a 340px
 *             column), help text folded into an info glyph so a row is one
 *             line. Built for going fast.
 *   panelen   master-detail on (almost) the whole screen: a fixed list of
 *             topics on the left, ONE topic on the right, roomy, no scrolling.
 *             Help text behind an info glyph unless "Toon uitleg" is on.
 *             Answers three demo complaints at once: "the modal does not use
 *             the screen", "the screens are busy", "too much scrolling".
 *   doorloop  panelen's look (grey stage, white surfaces, full screen) with
 *             werkblad's behaviour: every topic is its own surface, stacked,
 *             one scroll; the index on the left follows along.
 *
 * The choice is stored in localStorage and only exposed to SoftTouch users
 * (the provider's `enabled`); everyone else stays on classic.
 */

export const DESIGN_VARIANTS = ['classic', 'werkblad', 'tabel', 'panelen', 'doorloop'];
const STORAGE_KEY = 'sft_webshop_design_variant';
const HELP_KEY = 'sft_webshop_design_help';
const PANEL_KEY = 'sft_webshop_panel_';

// ---- tokens -----------------------------------------------------------------
// Everything below is derived from (variant, isDark). Kept as plain functions
// so consumers get stable objects per (variant, mode) via useMemo.

const PAPER = {
    werkblad: { light: '#FBFBF9', dark: '#1E2430' },
};
const RULE = { light: '#E3E5E8', dark: '#3A4654' };
const SHEET_HEAD = { light: '#F3F5F7', dark: '#283140' };

const resolveTokens = (variant, isDark, showHelp) => {
    const rule = isDark ? RULE.dark : RULE.light;
    switch (variant) {
        case 'werkblad':
            return {
                variant,
                // one reading column: the eye never has to guess the order
                modalColumns: { xs: '1' },
                flowGap: 3,
                rail: true,
                railWidth: 200,
                // no inner cap: the dialog itself is sized to one reading column
                // (rail + ~700px), so the content fills it and nothing is left blank
                contentMaxWidth: null,
                dialog: {
                    paperSx: { backgroundColor: isDark ? PAPER.werkblad.dark : PAPER.werkblad.light, borderRadius: 2, width: '90vw', maxWidth: 1000 },
                    titleSx: { px: 3, pt: 2.5, pb: 1.5, textTransform: 'none' },
                    titleTypography: { fontSize: '1.375rem', fontWeight: 700, lineHeight: 1.2 },
                    subtitleSx: { fontSize: '0.875rem', mt: 0.25 },
                    contentSx: { px: 3, py: 2 },
                    actionsSx: { px: 3, py: 1.5 },
                    darkHeader: false,
                },
                // the card dissolves: a heading with a hairline under it, no box
                card: {
                    borderRadius: 0,
                    border: 'none',
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    p: 0,
                    '&:hover': { boxShadow: 'none' },
                },
                cardHeader: { borderBottom: `1px solid ${rule}`, mb: 1, pb: 0.75 },
                cardTitle: { fontSize: '1.0625rem', fontWeight: 700, lineHeight: 1.25 },
                cardDescription: { fontSize: '0.8125rem', mt: 0.25, lineHeight: 1.45 },
                cardBodySpacing: 0.5,
                row: 'grid',
                rowSx: {
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) 280px',
                    alignItems: 'center',
                    columnGap: 2,
                    py: 1.25,
                    borderBottom: `1px solid ${rule}`,
                    '&:last-of-type': { borderBottom: 'none' },
                },
                // every control ends on the column's right edge: one alignment line
                rowControlSx: { justifySelf: 'end', display: 'flex', justifyContent: 'flex-end', minWidth: 0, maxWidth: '100%' },
                rowLabelSx: { fontSize: '0.9rem', fontWeight: 600 },
                rowHelpSx: { fontSize: '0.78rem', lineHeight: 1.45, mt: 0.25 },
                sublabel: {
                    textTransform: 'none',
                    letterSpacing: 0,
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    color: 'text.primary',
                },
            };
        case 'tabel':
            return {
                variant,
                // one sheet, top to bottom; two columns would split the ledger
                modalColumns: { xs: '1' },
                flowGap: 0,
                rail: false,
                contentMaxWidth: null,
                dialog: {
                    paperSx: { borderRadius: 1, width: '90vw', maxWidth: 980 },
                    // the strip is the only coloured element: it separates the
                    // dialog from the header rows below, which are light grey
                    titleSx: { px: 2, py: 1, backgroundColor: Colors.sft_blue, color: '#fff', textTransform: 'none' },
                    titleTypography: { fontSize: '0.9375rem', fontWeight: 700, lineHeight: 1.3, color: '#fff' },
                    // the subtitle repeats what the title says; in a sheet every line costs
                    subtitleSx: { display: 'none' },
                    contentSx: { px: 0, py: 0 },
                    actionsSx: { px: 1.5, py: 0.75 },
                    darkHeader: true,
                },
                // no card at all -- the topic is a header ROW in the sheet
                card: {
                    borderRadius: 0,
                    border: 'none',
                    boxShadow: 'none',
                    backgroundColor: 'transparent',
                    p: 0,
                    '&:hover': { boxShadow: 'none' },
                },
                cardHeader: {
                    backgroundColor: isDark ? SHEET_HEAD.dark : SHEET_HEAD.light,
                    borderBottom: `1px solid ${rule}`,
                    px: 2,
                    py: 0.625,
                    mb: 0,
                    pb: 0.625,
                    alignItems: 'center',
                },
                cardTitle: { fontSize: '0.6875rem', fontWeight: 700, lineHeight: 1.4, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.secondary' },
                cardDescription: { display: 'none' },
                // the body carries the horizontal padding; rows bleed back out so
                // their hairlines run the full width of the sheet
                cardBody: { px: 2, py: 0 },
                cardBodySpacing: 0,
                row: 'sheet',
                rowSx: {
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) 340px',
                    alignItems: 'center',
                    columnGap: 2,
                    minHeight: 38,
                    py: 0.375,
                    mx: -2,
                    px: 2,
                    borderBottom: `1px solid ${rule}`,
                },
                // controls form one right-aligned column; type shrinks with the row
                rowControlSx: {
                    justifySelf: 'end',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    minWidth: 0,
                    maxWidth: '100%',
                    '& .MuiInputBase-root': { fontSize: '0.8125rem' },
                    '& .MuiInputBase-input': { py: 0.625 },
                    '& .MuiButton-root, & .MuiChip-root': { fontSize: '0.75rem' },
                },
                rowLabelSx: { fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.3 },
                rowHelpSx: null,
                // one line per setting: the explanation moves into an info glyph
                helpAsTooltip: true,
                sublabel: {
                    textTransform: 'none',
                    letterSpacing: 0,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: 'text.secondary',
                    mb: 0.5,
                    pb: 0.25,
                },
            };
        case 'panelen':
        case 'doorloop': {
            // the panel is a white surface on a grey stage: the frame can use
            // the whole screen without the settings floating in a void
            const surface = {
                backgroundColor: isDark ? '#2D333B' : '#FFFFFF',
                border: `1px solid ${rule}`,
                borderRadius: 2,
                px: 4,
                py: 3,
            };
            const base = {
                variant,
                modalColumns: { xs: '1' },
                flowGap: 0,
                rail: true,
                railMode: 'switch',
                railWidth: 240,
                railBorder: false,
                helpToggle: true,
                contentMaxWidth: null,
                contentSurface: { ...surface, minHeight: '100%' },
                dialog: {
                    // the whole screen, and a fixed height so switching panels
                    // does not make the dialog jump
                    paperSx: { borderRadius: 2, width: '94vw', maxWidth: 1700, height: '92vh', backgroundColor: isDark ? '#1E2430' : '#F4F6F8' },
                    titleSx: { px: 3, pt: 2, pb: 1.5, textTransform: 'none' },
                    titleTypography: { fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.2 },
                    subtitleSx: { fontSize: '0.85rem', mt: 0.25 },
                    contentSx: { px: 3, py: 2.5 },
                    actionsSx: { px: 3, py: 1.5 },
                    darkHeader: false,
                },
                // one topic on screen: it needs no box, the title IS the panel title
                card: {
                    borderRadius: 0,
                    border: 'none',
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    p: 0,
                    '&:hover': { boxShadow: 'none' },
                },
                cardHeader: { borderBottom: `1px solid ${rule}`, mb: 1.5, pb: 1 },
                cardTitle: { fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.25 },
                cardDescription: showHelp ? { fontSize: '0.875rem', mt: 0.5, lineHeight: 1.5 } : { display: 'none' },
                cardBodySpacing: 0.5,
                row: 'grid',
                rowSx: {
                    display: 'grid',
                    // label on the left, the control column takes the right ~40%:
                    // inputs stretch to fill it, switches sit on its right edge
                    gridTemplateColumns: 'minmax(0, 1fr) minmax(320px, 40%)',
                    alignItems: 'center',
                    columnGap: 4,
                    py: 1.5,
                    borderBottom: `1px solid ${rule}`,
                    '&:last-of-type': { borderBottom: 'none' },
                },
                rowControlSx: {
                    justifySelf: 'stretch',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    minWidth: 0,
                    '& > .MuiTextField-root, & > .MuiFormControl-root, & > .MuiAutocomplete-root': { flex: 1 },
                },
                rowLabelSx: { fontSize: '0.95rem', fontWeight: 600 },
                rowHelpSx: { fontSize: '0.8125rem', lineHeight: 1.45, mt: 0.25 },
                // quiet by default; the rail has a "Toon uitleg" switch
                helpAsTooltip: !showHelp,
                sublabel: {
                    textTransform: 'none',
                    letterSpacing: 0,
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    color: 'text.primary',
                },
            };
            if (variant === 'doorloop') {
                // same look, but every topic is its own surface in one scroll and
                // the index follows the scroll instead of switching
                return {
                    ...base,
                    railMode: 'spy',
                    flowGap: 2,
                    contentSurface: null,
                    card: { ...surface, boxShadow: 'none', '&:hover': { boxShadow: 'none', borderColor: rule } },
                };
            }
            return base;
        }
        default:
            // classic: every primitive falls back to its own styling
            return {
                variant: 'classic',
                modalColumns: { xs: '1', lg: '2' },
                flowGap: 2,
                rail: false,
                contentMaxWidth: null,
                dialog: {
                    paperSx: {},
                    // textTransform: the global `h2 { text-transform: lowercase }`
                    // in main.css would otherwise lowercase the section title
                    titleSx: { py: 1.25, px: 2, textTransform: 'none' },
                    titleTypography: { fontWeight: 600, lineHeight: 1.3, fontSize: '1.1rem' },
                    subtitleSx: { fontSize: '0.78rem', mt: 0.25 },
                    contentSx: { px: 3, py: 2 },
                    actionsSx: { px: 1.5, py: 0.75 },
                    darkHeader: false,
                },
                card: null,
                cardHeader: null,
                cardTitle: null,
                cardDescription: null,
                cardBody: null,
                cardBodySpacing: 2,
                row: 'stack',
                rowSx: null,
                rowControlSx: null,
                rowLabelSx: null,
                rowHelpSx: null,
                sublabel: null,
            };
    }
};

// ---- context ----------------------------------------------------------------

const DesignVariantContext = createContext({ variant: 'classic', setVariant: () => {}, enabled: false, showHelp: false, setShowHelp: () => {} });

const readStored = () => {
    try {
        const v = window.localStorage.getItem(STORAGE_KEY);
        // 'kassa' was dropped 2026-09-10; its dark title strip lives on in tabel
        if (v === 'kassa') return 'tabel';
        return DESIGN_VARIANTS.includes(v) ? v : 'classic';
    } catch {
        return 'classic';
    }
};

const readHelp = () => {
    try { return window.localStorage.getItem(HELP_KEY) === '1'; } catch { return false; }
};

export const DesignVariantProvider = ({ enabled = false, children }) => {
    const [variant, setVariantState] = useState(readStored);
    const [showHelp, setShowHelpState] = useState(readHelp);
    const setShowHelp = useCallback((on) => {
        setShowHelpState(!!on);
        try { window.localStorage.setItem(HELP_KEY, on ? '1' : '0'); } catch { /* ignore */ }
    }, []);
    const setVariant = useCallback((next) => {
        const v = DESIGN_VARIANTS.includes(next) ? next : 'classic';
        setVariantState(v);
        try { window.localStorage.setItem(STORAGE_KEY, v); } catch { /* private mode etc. */ }
    }, []);
    // A stored non-classic choice must not leak to accounts that are not
    // allowed to switch (shared browser, changed rights).
    const effective = enabled ? variant : 'classic';
    const value = useMemo(() => ({ variant: effective, setVariant, enabled, showHelp, setShowHelp }), [effective, setVariant, enabled, showHelp, setShowHelp]);
    return <DesignVariantContext.Provider value={value}>{children}</DesignVariantContext.Provider>;
};
DesignVariantProvider.propTypes = { enabled: PropTypes.bool, children: PropTypes.node };

/**
 * Current variant + its resolved tokens for the active MUI palette mode.
 * Safe without a provider (falls back to classic), so primitives can be
 * rendered anywhere.
 */
export const useDesignVariant = () => {
    const ctx = useContext(DesignVariantContext);
    const isDark = useTheme().palette.mode === 'dark';
    const tokens = useMemo(() => resolveTokens(ctx.variant, isDark, ctx.showHelp), [ctx.variant, isDark, ctx.showHelp]);
    return { variant: ctx.variant, setVariant: ctx.setVariant, enabled: ctx.enabled, isDark, tokens, showHelp: ctx.showHelp, setShowHelp: ctx.setShowHelp };
};

// ---- switch -------------------------------------------------------------------

/**
 * Segmented control to switch variants. Renders nothing when the provider is
 * not enabled for this user. `t` is optional; falls back to the Dutch labels.
 * Lives in the tab bar / page header only — inside a modal it would compete
 * with the title.
 */
export const DesignVariantSwitch = ({ t, size = 'small', sx = {} }) => {
    const { variant, setVariant, enabled } = useDesignVariant();
    if (!enabled) return null;
    const tr = (k, fb) => (typeof t === 'function' ? t(k, fb) : fb);
    const labels = {
        classic:  tr('ui_design_variant_classic',  'Klassiek'),
        werkblad: tr('ui_design_variant_werkblad', 'Werkblad'),
        tabel:    tr('ui_design_variant_tabel',    'Tabel'),
        panelen:  tr('ui_design_variant_panelen',  'Panelen'),
        doorloop: tr('ui_design_variant_doorloop', 'Doorloop'),
    };
    return (
        <Tooltip title={tr('ui_design_variant_help', 'Demo: wissel de vormgeving van de instellingenschermen. Alleen zichtbaar voor SoftTouch.')}>
            <ToggleButtonGroup
                size={size}
                exclusive
                value={variant}
                onChange={(_e, v) => { if (v) setVariant(v); }}
                aria-label={tr('ui_design_variant_label', 'Weergave')}
                sx={{
                    '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        px: 1.25,
                        py: 0.25,
                        fontSize: '0.75rem',
                        lineHeight: 1.6,
                    },
                    ...sx,
                }}
            >
                {DESIGN_VARIANTS.map(v => <ToggleButton key={v} value={v}>{labels[v]}</ToggleButton>)}
            </ToggleButtonGroup>
        </Tooltip>
    );
};
DesignVariantSwitch.propTypes = { t: PropTypes.func, size: PropTypes.string, sx: PropTypes.object };

// ---- layout helpers -----------------------------------------------------------

/**
 * The card flow inside a wide modal. Classic keeps the two masonry columns;
 * werkblad and tabel read top to bottom in one column (tabel with no gap, so
 * the header rows stack into one sheet).
 */
export const ModalCardFlow = ({ children, sx = {} }) => {
    const { tokens } = useDesignVariant();
    return (
        <Box sx={{ columnGap: 2, columns: tokens.modalColumns, '& > *': { breakInside: 'avoid', mb: tokens.flowGap }, ...sx }}>
            {children}
        </Box>
    );
};
ModalCardFlow.propTypes = { children: PropTypes.node, sx: PropTypes.object };

/**
 * Section index for the werkblad variant: lists the cards found inside the
 * dialog body (anything with an id starting with `cfg-card-`) and highlights
 * the one currently at the top of the scroll area. Click = scroll there.
 *
 * Reads the DOM after render instead of asking every modal to declare its
 * sections: the cards already carry stable ids for the config search, and
 * permissions decide at render time which ones exist.
 */
export const SectionRail = ({ scrollRef, deps = [], t, selected = null, onSelect, onItems }) => {
    const { tokens, isDark, showHelp, setShowHelp } = useDesignVariant();
    const [items, setItems] = useState([]);
    const [active, setActive] = useState(null);
    const tr = (k, fb) => (typeof t === 'function' ? t(k, fb) : fb);
    // 'switch' (panelen): the rail picks which card is shown; 'spy' (werkblad):
    // the rail follows the scroll position.
    const isSwitch = tokens.railMode === 'switch';

    const collect = useCallback(() => {
        const root = scrollRef.current;
        if (!root) return;
        const found = Array.from(root.querySelectorAll('[id^="cfg-card-"]')).map(el => {
            const titleEl = el.querySelector('.MuiTypography-subtitle1, .MuiTypography-h6');
            return { id: el.id, label: (titleEl?.textContent || el.id).trim() };
        }).filter(i => i.label);
        // only re-render when the index really changed: the observer below
        // fires on every DOM change inside the body, including our own
        setItems(prev => (prev.length === found.length && prev.every((it, i) => it.id === found[i].id && it.label === found[i].label)) ? prev : found);
    }, [scrollRef]);

    // Scan now and again shortly after: the modal bodies are lazy chunks and
    // the dialog fades in, so the cards can land a beat after this commit.
    useLayoutEffect(() => {
        collect();
        const timers = [60, 250, 800].map(ms => window.setTimeout(collect, ms));
        return () => timers.forEach(id => window.clearTimeout(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collect, ...deps]);

    // And keep watching: permissions or a language switch can add/remove cards.
    useEffect(() => {
        const root = scrollRef.current;
        if (!root || typeof MutationObserver === 'undefined') return undefined;
        const mo = new MutationObserver(() => collect());
        mo.observe(root, { childList: true, subtree: true });
        return () => mo.disconnect();
    }, [scrollRef, collect]);

    // report the index upward (the panel switcher needs it to hide the others)
    useEffect(() => { if (onItems) onItems(items); }, [items, onItems]);

    // switch mode: make sure something is selected once the index is known
    useEffect(() => {
        if (!isSwitch || !onSelect || items.length === 0) return;
        if (!selected || !items.some(i => i.id === selected)) onSelect(items[0].id);
    }, [isSwitch, items, selected, onSelect]);

    useEffect(() => {
        const root = scrollRef.current;
        if (!root || isSwitch) return undefined;
        const onScroll = () => {
            const top = root.getBoundingClientRect().top + 24;
            let current = null;
            root.querySelectorAll('[id^="cfg-card-"]').forEach(el => {
                if (el.getBoundingClientRect().top <= top + 8) current = el.id;
            });
            setActive(current || (items[0] && items[0].id) || null);
        };
        onScroll();
        root.addEventListener('scroll', onScroll, { passive: true });
        return () => root.removeEventListener('scroll', onScroll);
    }, [scrollRef, items, isSwitch]);

    if (!tokens.rail || items.length < 2) return null;

    const jump = (id) => {
        if (isSwitch) { if (onSelect) onSelect(id); return; }
        const root = scrollRef.current;
        const el = root?.querySelector(`#${CSS.escape(id)}`);
        if (!root || !el) return;
        const offset = el.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop - 8;
        root.scrollTo({ top: offset, behavior: 'smooth' });
    };

    const rule = isDark ? RULE.dark : RULE.light;
    return (
        <Box component="nav" aria-label={tr('ui_design_rail_label', 'Onderdelen')} sx={{
            width: tokens.railWidth || 200,
            flexShrink: 0,
            alignSelf: 'flex-start',
            position: 'sticky',
            top: 0,
            pr: 2,
            mr: 3,
            borderRight: tokens.railBorder === false ? 'none' : `1px solid ${rule}`,
        }}>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1, fontSize: '0.75rem' }}>
                {tr('ui_design_rail_label', 'Onderdelen')}
            </Typography>
            {items.map(item => {
                const isActive = isSwitch ? item.id === selected : item.id === active;
                return (
                    <Box
                        key={item.id}
                        component="button"
                        type="button"
                        onClick={() => jump(item.id)}
                        sx={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            font: 'inherit',
                            fontSize: '0.875rem',
                            lineHeight: 1.35,
                            py: 0.625,
                            pl: 1.25,
                            color: isActive ? Colors.sft_blue : 'text.primary',
                            fontWeight: isActive ? 700 : 500,
                            borderLeft: `2px solid ${isActive ? Colors.sft_blue : 'transparent'}`,
                            ml: '-1px',
                            '&:hover': { color: Colors.sft_blue },
                            '&:focus-visible': { outline: `2px solid ${Colors.sft_blue}`, outlineOffset: 2, borderRadius: 0.5 },
                        }}
                    >
                        {item.label}
                    </Box>
                );
            })}
            {tokens.helpToggle && (
                <FormControlLabel
                    sx={{ mt: 2, ml: 0.5, '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: 'text.secondary' } }}
                    control={<Switch size="small" checked={!!showHelp} onChange={(e) => setShowHelp(e.target.checked)} />}
                    label={tr('ui_design_help_toggle', 'Toon uitleg')}
                />
            )}
        </Box>
    );
};
SectionRail.propTypes = { scrollRef: PropTypes.object.isRequired, deps: PropTypes.array, t: PropTypes.func, selected: PropTypes.string, onSelect: PropTypes.func, onItems: PropTypes.func };

/**
 * Body of a config dialog: optional section rail + a scroll area for the
 * content. Replaces a bare <DialogContent>. The rail only appears for the
 * werkblad variant and only when there are at least two sections; werkblad
 * also caps the content column (`contentMaxWidth`).
 */
export const DialogBody = ({ children, deps = [], t, sx = {}, panelKey = '', scrollRef: externalRef = null }) => {
    const { tokens } = useDesignVariant();
    const innerRef = useRef(null);
    // a caller that needs the scroll node itself (fullscreen editor restoring
    // its scroll position) passes its own ref
    const scrollRef = externalRef || innerRef;
    const isSwitch = tokens.railMode === 'switch';

    // panelen: which card is on screen. Remembered per modal so a consultant
    // who keeps coming back to "Navigatiemenu" lands there.
    const readPanel = (key) => {
        try { return window.localStorage.getItem(PANEL_KEY + key) || null; } catch { return null; }
    };
    const [selected, setSelected] = useState(() => readPanel(panelKey));
    const [itemCount, setItemCount] = useState(0);
    const onItems = useCallback((items) => setItemCount(items.length), []);
    const onSelect = useCallback((id) => {
        setSelected(id);
        try { window.localStorage.setItem(PANEL_KEY + panelKey, id); } catch { /* ignore */ }
        // portalled editors (Unlayer in the container dialog) position
        // themselves from their slot's rect; a panel switch moves that slot
        // without a window resize, so give them the nudge they listen for
        window.requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    }, [panelKey]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { setSelected(readPanel(panelKey)); }, [panelKey]);

    // a search hit for a card in another panel opens that panel first
    useEffect(() => {
        if (!isSwitch) return undefined;
        return subscribeAnchor((id) => {
            const root = scrollRef.current;
            if (root && root.querySelector(`#${CSS.escape(id)}`)) onSelect(id);
        });
    }, [isSwitch, onSelect, scrollRef]);

    // hide every card except the selected one (and any card that contains it)
    const panelSx = isSwitch && selected && itemCount >= 2 ? {
        '& [id^="cfg-card-"]': { display: 'none' },
        [`& #${CSS.escape(selected)}, & [id^="cfg-card-"]:has(#${CSS.escape(selected)})`]: { display: 'block' },
    } : {};

    return (
        <Box sx={{ display: 'flex', minHeight: 0, flex: 1, ...tokens.dialog.contentSx, ...sx }}>
            <SectionRail scrollRef={scrollRef} deps={deps} t={t} selected={selected} onSelect={onSelect} onItems={onItems} />
            <Box ref={scrollRef} sx={{ flex: 1, minWidth: 0, overflowY: 'auto', pr: tokens.rail ? 1 : 0, ...panelSx }}>
                {/* the cap only applies when the rail is there; a lone editor
                    (carousel slides, image grid) gets the whole width */}
                <Box sx={{ maxWidth: (!isSwitch || itemCount >= 2) && tokens.contentMaxWidth ? tokens.contentMaxWidth : 'none', ...(tokens.contentSurface || {}) }}>
                    {children}
                </Box>
            </Box>
        </Box>
    );
};
DialogBody.propTypes = { children: PropTypes.node, deps: PropTypes.array, t: PropTypes.func, sx: PropTypes.object, panelKey: PropTypes.string, scrollRef: PropTypes.object };
